import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, StyleSheet, Platform, Pressable } from 'react-native';
import { useTheme, TYPOGRAPHY, ON_COLOR, SPACING, BORDER_RADIUS, withWeight } from '../../theme';
import { AdaptiveSheet, Button, ErrorBanner, Input, TextLink } from '../common';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthStore } from '../../store/authStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { useUIStore } from '../../store/uiStore';
import { appAlert } from '../../utils/appAlert';
import type { SignUpRole } from '../../types/api';
import { ANON_WRITES_ENABLED, MIN_PASSWORD_LENGTH } from '../../constants/auth';

const ROLES: { value: SignUpRole; label: string }[] = [
  { value: 'homeowner', label: 'Homeowner' },
  { value: 'business', label: 'Business' },
  { value: 'agent', label: 'Agent' },
];

function SignInForm({ onSuccess }: { onSuccess: () => void }) {
  const { colors } = useTheme();
  const { signIn, sendPasswordResetEmail, isLoading, error, clearError } = useAuthStore();
  const setView = useAuthGateStore((s) => s.setView);
  const passwordRef = useRef<TextInput>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSignIn = async () => {
    if (!email || !password) return;
    try {
      await signIn({ email, password });
      onSuccess();
    } catch {
      // Error handled by store
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      appAlert('Enter Email', 'Please enter your email address first, then tap "Forgot password?"');
      return;
    }
    try {
      await sendPasswordResetEmail(email);
      appAlert('Check Your Email', `A password reset link has been sent to ${email}`);
    } catch {
      appAlert('Error', 'Could not send reset email. Please check the address and try again.');
    }
  };

  return (
    <View>
      <Text style={[styles.sheetTitle, { color: colors.text }]}>Sign in</Text>

      <Input
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="Enter your email"
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="next"
        onSubmitEditing={() => passwordRef.current?.focus()}
        blurOnSubmit={false}
        testID="authgate-email"
      />

      <Input
        ref={passwordRef}
        label="Password"
        value={password}
        onChangeText={setPassword}
        placeholder="Enter your password"
        secureTextEntry
        returnKeyType="done"
        onSubmitEditing={handleSignIn}
        testID="authgate-password"
      />

      <TextLink
        label="Forgot password?"
        onPress={handleForgotPassword}
        style={styles.forgotPassword}
        textStyle={styles.forgotPasswordText}
      />

      <ErrorBanner message={error} style={styles.errorSpacing} />

      <Button
        title="Sign In"
        onPress={handleSignIn}
        loading={isLoading}
        disabled={!email || !password}
        fullWidth
        size="lg"
        testID="authgate-submit"
      />

      <View style={styles.switchRow}>
        <Text style={[styles.switchText, { color: colors.textSecondary }]}>
          Don&apos;t have an account?{' '}
        </Text>
        <TextLink
          label="Sign up"
          onPress={() => setView('sign-up')}
          textStyle={styles.switchLink}
        />
      </View>
    </View>
  );
}

function SignUpForm({ onSuccess }: { onSuccess: () => void }) {
  const { colors } = useTheme();
  const { signUp, isLoading, error, clearError } = useAuthStore();
  const setView = useAuthGateStore((s) => s.setView);
  const lastNameRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);
  const extraRef = useRef<TextInput>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<SignUpRole>('homeowner');
  const [insurerName, setInsurerName] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  useEffect(() => {
    clearError();
  }, [clearError]);

  const isAgent = role === 'agent';

  const handleSignUp = async () => {
    if (!firstName || !lastName || !email || !password) return;
    if (isAgent && !insurerName.trim()) return;
    try {
      await signUp({
        email,
        password,
        firstName,
        lastName,
        roles: isAgent ? ['insurance-agent'] : ['property-owner'],
        insurerName: isAgent ? insurerName.trim() : undefined,
        inviteCode: !isAgent && inviteCode.trim() ? inviteCode.trim() : undefined,
      });
      onSuccess();
    } catch {
      // Error handled by store
    }
  };

  const isValid =
    firstName &&
    lastName &&
    email &&
    password.length >= MIN_PASSWORD_LENGTH &&
    (!isAgent || insurerName.trim().length > 0);

  return (
    <View>
      <Text style={[styles.sheetTitle, { color: colors.text }]}>Create account</Text>

      {/* Role picker */}
      <View style={styles.roleSection}>
        <Text style={[styles.roleLabel, { color: colors.textSecondary }]}>I am a...</Text>
        <View style={styles.roleRow}>
          {ROLES.map((r) => {
            const selected = role === r.value;
            return (
              <Pressable
                key={r.value}
                onPress={() => setRole(r.value)}
                style={[
                  styles.rolePill,
                  {
                    backgroundColor: selected ? colors.primary : 'transparent',
                    borderColor: selected ? colors.primary : colors.border,
                  },
                ]}>
                <Text
                  style={[styles.rolePillText, { color: selected ? ON_COLOR.text : colors.text }]}>
                  {r.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={styles.nameRow}>
        <View style={styles.nameField}>
          <Input
            label="First name"
            value={firstName}
            onChangeText={setFirstName}
            placeholder="First"
            autoCapitalize="words"
            returnKeyType="next"
            onSubmitEditing={() => lastNameRef.current?.focus()}
            blurOnSubmit={false}
          />
        </View>
        <View style={styles.nameField}>
          <Input
            ref={lastNameRef}
            label="Last name"
            value={lastName}
            onChangeText={setLastName}
            placeholder="Last"
            autoCapitalize="words"
            returnKeyType="next"
            onSubmitEditing={() => emailRef.current?.focus()}
            blurOnSubmit={false}
          />
        </View>
      </View>

      <Input
        ref={emailRef}
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="Enter your email"
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="next"
        onSubmitEditing={() => passwordRef.current?.focus()}
        blurOnSubmit={false}
      />

      <Input
        ref={passwordRef}
        label="Password"
        value={password}
        onChangeText={setPassword}
        placeholder="Create a password"
        secureTextEntry
        returnKeyType="next"
        onSubmitEditing={() => extraRef.current?.focus()}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
      />

      {isAgent ? (
        <Input
          ref={extraRef}
          label="Insurance company name"
          value={insurerName}
          onChangeText={setInsurerName}
          placeholder="e.g. Acme Mutual"
          autoCapitalize="words"
          autoCorrect={false}
          returnKeyType="done"
          onSubmitEditing={handleSignUp}
        />
      ) : (
        // The code an agent hands a client, which links this account to that
        // agent (src/api/auth.ts validates it before the user is created).
        // It used to read "Agent invite code (optional)", which a homeowner
        // reads as a question about whether they are an agent.
        <Input
          ref={extraRef}
          label="Invite code from your insurance agent"
          value={inviteCode}
          onChangeText={setInviteCode}
          placeholder="Optional"
          autoCapitalize="characters"
          autoCorrect={false}
          returnKeyType="done"
          onSubmitEditing={handleSignUp}
        />
      )}

      <ErrorBanner message={error} style={styles.errorSpacing} />

      <Button
        title="Create Account"
        onPress={handleSignUp}
        loading={isLoading}
        disabled={!isValid}
        fullWidth
        size="lg"
      />

      <View style={styles.switchRow}>
        <Text style={[styles.switchText, { color: colors.textSecondary }]}>
          Already have an account?{' '}
        </Text>
        <TextLink
          label="Sign in"
          onPress={() => setView('sign-in')}
          textStyle={styles.switchLink}
        />
      </View>
    </View>
  );
}

function ScoutCTA({ onSuccess }: { onSuccess: () => void }) {
  const { colors } = useTheme();
  const signInAsScout = useAuthStore((s) => s.signInAsScout);
  const [busy, setBusy] = useState(false);

  const handlePress = async () => {
    setBusy(true);
    try {
      await signInAsScout();
      onSuccess();
    } catch {
      // Surfaced via authStore error state in the forms below.
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={styles.scoutCta}>
      <Text style={[styles.scoutTitle, { color: colors.text }]}>Continue as Fire Scout</Text>
      <Text style={[styles.scoutBlurb, { color: colors.textSecondary }]}>
        Post and comment without signing up. You can save your account later.
      </Text>
      <Button
        title="I'm a Fire Scout"
        onPress={handlePress}
        loading={busy}
        fullWidth
        size="lg"
        testID="authgate-scout-cta"
      />
    </View>
  );
}

export function AuthGateBottomSheet() {
  const { colors } = useTheme();
  const { isOpen, view, message, close, executePending } = useAuthGateStore();
  const takeReturnPath = useAuthGateStore((s) => s.takeReturnPath);
  const { showTabBar } = useUIStore();
  const [googleBusy, setGoogleBusy] = useState(false);

  // Pre-signup education: swap the gate for the Tour REDI sheet. The tour's
  // own CTA routes signed-out users back here, so nobody dead-ends.
  const handleOpenTour = useCallback(() => {
    close();
    useUIStore.getState().openSheet('howItWorks');
  }, [close]);

  // Onboarding is no longer forced, so the post-auth route guard never
  // intercepts the pending callback. Always honor the intent that opened
  // the gate (claim a property, post, etc.).
  const handleSignInSuccess = useCallback(() => {
    showTabBar();
    takeReturnPath();
    executePending();
  }, [showTabBar, takeReturnPath, executePending]);

  const handleSignUpSuccess = useCallback(() => {
    showTabBar();
    takeReturnPath();
    executePending();
  }, [showTabBar, takeReturnPath, executePending]);

  const handleGoogle = async () => {
    setGoogleBusy(true);
    try {
      const ok = await useAuthStore.getState().signInWithGoogle();
      if (ok) handleSignInSuccess();
    } catch {
      // Error surfaces via authStore.error, rendered by the forms' ErrorBanner below.
    } finally {
      setGoogleBusy(false);
    }
  };

  return (
    <AdaptiveSheet
      isOpen={isOpen}
      onRequestClose={close}
      onDismiss={close}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      androidKeyboardInputMode="adjustResize">
      <AdaptiveSheet.ScrollView
        testID="authgate-sheet"
        contentContainerStyle={styles.sheetContent}
        keyboardShouldPersistTaps="handled">
        {message && (
          <Text style={[styles.messageText, { color: colors.textSecondary }]}>{message}</Text>
        )}
        {/* Scout CTA (and its "or" divider) is hidden while anonymous
            writes are flag-disabled — a scout account could take no
            meaningful action. See ANON_WRITES_ENABLED. */}
        {ANON_WRITES_ENABLED && <ScoutCTA onSuccess={handleSignInSuccess} />}
        <TextLink
          label="New to REDI? See how it works"
          onPress={handleOpenTour}
          accessibilityLabel="See how REDI works"
          testID="authgate-whats-redi"
          hitSlop={8}
          style={styles.tourLink}
          textStyle={styles.tourLinkText}
        />
        {ANON_WRITES_ENABLED && (
          <View style={styles.dividerRow}>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
            <Text style={[styles.dividerText, { color: colors.textTertiary }]}>or</Text>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
          </View>
        )}
        {/* Shared by both sign-in and sign-up views below — Google doesn't
            distinguish the two the way the email forms do. Wrapper is
            web-gated too: the button renders null on native, but the wrap's
            marginBottom would still add phantom spacing. */}
        {Platform.OS === 'web' && (
          <View style={styles.googleButtonWrap}>
            <GoogleSignInButton
              testID="authgate-google"
              loading={googleBusy}
              onPress={handleGoogle}
            />
          </View>
        )}
        {view === 'sign-in' ? (
          <SignInForm onSuccess={handleSignInSuccess} />
        ) : (
          <SignUpForm onSuccess={handleSignUpSuccess} />
        )}
        {/* Scroll sentinel: Maestro flows assert this is reachable. */}
        <View testID="authgate-sheet-end" style={styles.scrollSentinel} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  sheetContent: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  messageText: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  sheetTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.lg,
  },
  forgotPassword: {
    alignSelf: 'flex-end',
    marginBottom: SPACING.lg,
    marginTop: -SPACING.sm,
  },
  forgotPasswordText: {
    ...TYPOGRAPHY.label,
  },
  errorSpacing: {
    marginBottom: SPACING.md,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: SPACING.lg,
  },
  switchText: {
    ...TYPOGRAPHY.body,
  },
  switchLink: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  roleSection: {
    marginBottom: SPACING.md,
  },
  roleLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  roleRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  rolePill: {
    flex: 1,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
  },
  rolePillText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  nameRow: {
    flexDirection: 'row',
    gap: SPACING.smd,
  },
  nameField: {
    flex: 1,
  },
  scoutCta: {
    marginBottom: SPACING.sm,
  },
  tourLink: {
    alignSelf: 'center',
    paddingVertical: SPACING.xs,
    marginBottom: SPACING.sm,
  },
  tourLinkText: {
    ...withWeight(TYPOGRAPHY.label, 'semibold'),
  },
  scoutTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.xs,
  },
  scoutBlurb: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SPACING.lg,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    ...TYPOGRAPHY.bodySmall,
    marginHorizontal: SPACING.md,
  },
  googleButtonWrap: {
    marginBottom: SPACING.lg,
  },
  scrollSentinel: {
    height: 1,
  },
});
